import axios from 'axios';
import { secureLocalStorage } from '../../utils/crypto';
import { getTenantId, getTenantConfig } from '../tenant/tenantService.js';
import { getAssetsByType } from '../core/assetsService.js';
import { userService } from './userService.js';
import { hasSegmentMatch } from '../core/segmentHelper.js';

const PARTICIPANTS_URL = process.env.REACT_APP_PARTICIPANTS_URL;

const getApiHeaders = () => ({
  'X-Tenant-Id': getTenantId(),
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${secureLocalStorage.getItem('idToken')}`
});

const api = axios.create({
  baseURL: PARTICIPANTS_URL,
});

const TERMS_ASSET_TYPES = [0, 1, 3];

const _contentCache = new Map();
const _pendingFetches = new Map();

const TEXT_EXTENSIONS = ['html', 'txt'];

/**
 * A API de Termos ainda não tem atributo nativo para restringir por profile, então o
 * mapeamento fica em FEATURE_FLAGS.TERMS_PROFILES (chave livre, repassada sem whitelist por
 * tenantService.js) até que a API de Termos suporte isso nativamente via term.profiles.
 * Formato: { "<idDoTermo>": ["profileValueA", "profileValueB"] }
 */
const getTermsProfilesMap = () => getTenantConfig()?.FEATURE_FLAGS?.TERMS_PROFILES || {};

const EMPTY_IDENTITY = { profile: null, values: [] };

let _participantIdentityCache = { token: null, identity: EMPTY_IDENTITY };
let _participantIdentityPromise = null;

/**
 * Identidades do participante usadas para elegibilidade de termos — sempre as duas fontes:
 * - `profile` e `segment` de GET /api/Participants/info (ex.: "gold" / "Esmeralda"; o claim
 *   `profile` do JWT não é usado)
 * - segmentos de GET /api/segmentParticipant/byParticipantUserName (nome, ex.: "Esmeralda"/"Legacy",
 *   e id, para termos segmentados nativamente no CMS via term.profiles)
 * TERMS_PROFILES mistura profile e nome de segmento. O participante pode ter vários segmentos, então
 * o byParticipantUserName é a lista completa; `info.segment` (valor único) é reforço caso ele falhe.
 * Cache por idToken + dedup de chamadas simultâneas (TermsModal roda em toda rota).
 * Falha em uma fonte → usa só a outra; falha nas duas → termos restritos ficam ocultos.
 */
const getParticipantIdentity = async () => {
  const token = secureLocalStorage.getItem('idToken');
  if (!token) return EMPTY_IDENTITY;
  if (_participantIdentityCache.token === token) return _participantIdentityCache.identity;

  if (!_participantIdentityPromise) {
    _participantIdentityPromise = Promise.allSettled([
      userService.getUserInfo(token),
      userService.getUserSegments(),
    ])
      .then(([infoResult, segmentsResult]) => {
        if (infoResult.status === 'rejected') {
          console.warn('Could not fetch participant profile for terms filtering:', infoResult.reason);
        }
        if (segmentsResult.status === 'rejected') {
          console.warn('Could not fetch participant segments for terms filtering:', segmentsResult.reason);
        }

        const info = infoResult.status === 'fulfilled' ? infoResult.value : null;
        const profile = info?.profile || null;
        const segments = segmentsResult.status === 'fulfilled' && Array.isArray(segmentsResult.value)
          ? segmentsResult.value
          : [];

        const values = [...new Set([
          profile,
          info?.segment,
          ...segments.map(s => s.name),
          ...segments.map(s => s.segmentId),
        ].filter(Boolean))];

        const identity = { profile, values };
        // Só cacheia com as duas fontes completas, para não fixar uma identidade incompleta —
        // getUserSegments engole erros e devolve [], então lista vazia não é cacheada.
        if (infoResult.status === 'fulfilled' && segments.length > 0) {
          _participantIdentityCache = { token, identity };
        }
        return identity;
      })
      .finally(() => {
        _participantIdentityPromise = null;
      });
  }
  return _participantIdentityPromise;
};

const resolveIdentity = async (profileOverride) =>
  profileOverride ? { profile: profileOverride, values: [profileOverride] } : getParticipantIdentity();

/**
 * Termo sem restrição configurada → visível para todos.
 * Termo restrito → visível se qualquer identidade do participante (profile do /info ou segmento)
 * estiver na lista. Reaproveita hasSegmentMatch (OR entre os valores configurados).
 */
const isTermVisibleForIdentity = (term, identityValues) => {
  const eligibleValues = term.profiles?.length ? term.profiles : getTermsProfilesMap()[term.id];
  return hasSegmentMatch(eligibleValues, identityValues);
};

const _fetchAndCache = (url) => {
  if (_pendingFetches.has(url)) return _pendingFetches.get(url);

  const promise = fetch(url)
    .then((res) => res.text())
    .then((text) => {
      _contentCache.set(url, text);
      return text;
    })
    .finally(() => {
      _pendingFetches.delete(url);
    });

  _pendingFetches.set(url, promise);
  return promise;
};

export const termsService = {
  preloadTermContent: (url) => {
    if (!url) return;
    const ext = url.split('.').pop().toLowerCase();
    if (!TEXT_EXTENSIONS.includes(ext)) return;
    if (_contentCache.has(url) || _pendingFetches.has(url)) return;
    _fetchAndCache(url).catch(() => {});
  },

  getTermContent: (url) => {
    if (_contentCache.has(url)) return Promise.resolve(_contentCache.get(url));
    return _fetchAndCache(url);
  },

  checkAcceptedTerms: async (assetIds) => {
    try {
      const response = await api.post(`/api/Participants/check-terms`,
        {
          assetIds: assetIds
        },
        {
          headers: getApiHeaders()
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error checking accepted terms:', error);
      throw error;
    }
  },

  getActiveTerms: async (language = 'pt-BR', profileOverride) => {
    try {
      const { profile, values: identityValues } = await resolveIdentity(profileOverride);
      // Busca assets do CMS usando o novo serviço unificado
      const activeTerms = await getAssetsByType(TERMS_ASSET_TYPES, {
        language,
        profile,
        isPublic: true,
        requireAuth: false, // Endpoint público não requer auth
      });
      
      // Filtra apenas assets que são realmente termos (validação adicional)
      const validTerms = activeTerms.filter(term => {
        // Se o asset tem a propriedade type, valida
        if (term.type !== undefined) {
          const isValid = TERMS_ASSET_TYPES.includes(term.type);
          if (!isValid) {
            console.warn('Asset filtered out - invalid type:', {
              id: term.id,
              type: term.type,
              validTypes: TERMS_ASSET_TYPES
            });
          }
          return isValid;
        }
        // Se não tem type, assume que é válido (pode ser que o backend não retorne)
        return true;
      });
      
      const profileTerms = validTerms.filter(term => isTermVisibleForIdentity(term, identityValues));

      const assetIds = profileTerms.map(term => term.id);
      if (assetIds.length === 0) {
        return [];
      }

      const acceptedTerms = await termsService.checkAcceptedTerms(assetIds);

      return profileTerms.filter(term => {
        const acceptedTerm = acceptedTerms.find(accepted => accepted.assetId === term.id);
        return !acceptedTerm?.isAccepted;
      });

    } catch (error) {
      console.error('Error fetching active terms:', error);
      throw error;
    }
  },

  getActiveTermsRegister: async (language = 'pt-BR', profileOverride) => {
    try {
      const { profile, values: identityValues } = await resolveIdentity(profileOverride);
      // Busca assets do CMS usando o novo serviço unificado
      const activeTerms = await getAssetsByType(TERMS_ASSET_TYPES, {
        language,
        profile,
        isPublic: true,
        requireAuth: false, // Endpoint público não requer auth
      });
      
      // Filtra apenas assets que são realmente termos (validação adicional)
      const validTerms = activeTerms.filter(term => {
        // Se o asset tem a propriedade type, valida
        if (term.type !== undefined) {
          const isValid = TERMS_ASSET_TYPES.includes(term.type);
          if (!isValid) {
            console.warn('Asset filtered out - invalid type:', {
              id: term.id,
              type: term.type,
              validTypes: TERMS_ASSET_TYPES
            });
          }
          return isValid;
        }
        // Se não tem type, assume que é válido (pode ser que o backend não retorne)
        return true;
      });

      return validTerms.filter(term => isTermVisibleForIdentity(term, identityValues));

    } catch (error) {
      console.error('Error fetching active terms:', error);
      throw error;
    }
  },

  getPublicActiveTerms: async (language = 'pt-BR', type = 0) => {
    try {
      // Busca assets públicos do CMS usando o novo serviço unificado
      const activeTerms = await getAssetsByType([type], {
        language,
        isPublic: true,
        requireAuth: false,
      });

      // Área deslogada (cadastro): nunca aplica profile, mesmo que o formulário já
      // tenha um PROFILE_SELECT escolhido — só termos sem restrição aparecem aqui.
      // Termos restritos por profile só aparecem depois do login (getActiveTerms/getActiveTermsRegister).
      return activeTerms.filter(term => isTermVisibleForIdentity(term, []));

    } catch (error) {
      console.error('Error fetching public active terms:', error);
      throw error;
    }
  },

  acceptTerm: async (assetId, asset = null) => {
    try {
      // Se o asset não foi passado, tenta buscar para validar o tipo
      let termAsset = asset;
      if (!termAsset && assetId) {
        // Valida se o assetId é válido antes de fazer a requisição
        console.log('Accepting term with assetId:', assetId);
        
        // Se temos o assetId mas não o objeto, precisamos validar
        // Vamos buscar o asset para verificar seu tipo
        try {
          const { getAssetById } = await import('../core/assetsService.js');
          termAsset = await getAssetById(assetId, false);
          
          // Valida se o asset é realmente um termo
          if (termAsset && termAsset.type !== undefined) {
            if (!TERMS_ASSET_TYPES.includes(termAsset.type)) {
              const error = new Error(`Invalid asset type: ${termAsset.type}. Expected one of: ${TERMS_ASSET_TYPES.join(', ')}`);
              error.code = 'INVALID_ASSET_TYPE';
              console.error('Error: Asset is not a valid term type', {
                assetId,
                assetType: termAsset.type,
                validTypes: TERMS_ASSET_TYPES
              });
              throw error;
            }
          } else {
            console.warn('Asset does not have type property, proceeding anyway', { assetId, asset: termAsset });
          }
        } catch (fetchError) {
          // Se não conseguir buscar o asset, continua mesmo assim
          // O backend vai validar
          console.warn('Could not fetch asset for validation, proceeding anyway', fetchError);
        }
      } else if (termAsset) {
        // Valida se o asset passado é realmente um termo
        if (termAsset.type !== undefined && !TERMS_ASSET_TYPES.includes(termAsset.type)) {
          const error = new Error(`Invalid asset type: ${termAsset.type}. Expected one of: ${TERMS_ASSET_TYPES.join(', ')}`);
          error.code = 'INVALID_ASSET_TYPE';
          console.error('Error: Asset is not a valid term type', {
            assetId,
            assetType: termAsset.type,
            validTypes: TERMS_ASSET_TYPES
          });
          throw error;
        }
      }

      const userAgent = navigator.userAgent;
      const browser = userAgent.split(' ').find(part => part.includes('/')) || 'Unknown';
      
      let geoLocation = { latitude: '0', longitude: '0' };
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
          });
          geoLocation = {
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString()
          };
        } catch (error) {
          console.warn('Geolocation error:', error);
        }
      }

      const response = await api.post(`/api/Participants/accept-terms`,
        {
          assetIds: [assetId],
          info: {
            fingerPrint: secureLocalStorage.getItem('fingerPrint') || 'unknown',
            browser,
            geoLocation
          }
        },
        {
          headers: getApiHeaders()
        }
      );
      return response.data;
    } catch (error) {
      // Melhora o tratamento de erro para mostrar mensagens mais claras
      if (error.response) {
        // Erro da API
        const apiError = error.response.data;
        if (Array.isArray(apiError) && apiError.length > 0) {
          const firstError = apiError[0];
          if (firstError.code === 'Participant.AcceptTermError' && firstError.description === 'Invalid asset type') {
            const enhancedError = new Error(`Tipo de asset inválido. O asset ${assetId} não é um termo válido.`);
            enhancedError.code = 'INVALID_ASSET_TYPE';
            enhancedError.originalError = firstError;
            console.error('Error accepting term - Invalid asset type:', {
              assetId,
              error: firstError,
              asset: asset
            });
            throw enhancedError;
          }
        }
        console.error('Error accepting term (API error):', {
          status: error.response.status,
          data: error.response.data,
          assetId
        });
        throw error;
      } else if (error.code === 'INVALID_ASSET_TYPE') {
        // Erro de validação local
        throw error;
      } else {
        // Outros erros
        console.error('Error accepting term:', error);
        throw error;
      }
    }
  }
}; 
